import { Box, Dialog, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography, Button, IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import { getQuestionResponses, getUserDataForResponses } from '../../../../utils/mutations/form';
import { Question } from '../../../../utils/types';

interface ResponsesViewProps {
    conferenceCode: string;
    question: Question;
    onClose: () => void;
}

const ResponsesView: React.FC<ResponsesViewProps> = ({ conferenceCode, question, onClose }) => {
    const [responses, setResponses] = useState<Record<string, any>>({});
    const [userData, setUserData] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchResponses = async () => {
            try {
                const questionResponses = await getQuestionResponses(conferenceCode, question.id);
                setResponses(questionResponses);

                const userIds = Object.keys(questionResponses);
                if (userIds.length > 0) {
                    const userData = await getUserDataForResponses(userIds);
                    setUserData(userData);
                }
            } catch (error) {
                console.error('Error fetching responses:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchResponses();
    }, [conferenceCode, question.id]);

    const handleDownload = () => {
        // Create CSV headers
        const headers = ['Name', 'Email', 'Response', 'Date'];
        
        // Create CSV data rows
        const csvData = Object.entries(responses).map(([userId, response]) => {
            const user = userData[userId];
            return [
                user?.displayName || 'Unknown User',
                user?.email || 'N/A',
                response.answer,
                new Date(response.timestamp?.toDate()).toLocaleString()
            ];
        });

        // Combine headers and data
        const csvContent = [headers, ...csvData]
            .map(row => row.map(cell => `"${cell}"`).join(','))
            .join('\n');

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${question.question.replace(/[^a-zA-Z0-9]/g, '_')}_responses.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ position: 'relative', pr: 8, pl: 6 }}>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        left: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                    size="large"
                >
                    <CloseIcon />
                </IconButton>
                Responses for: {question.question}
                <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    sx={{ float: 'right' }}
                    disabled={Object.keys(responses).length === 0}
                >
                    Download CSV
                </Button>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <Typography>Loading responses...</Typography>
                    </Box>
                ) : Object.keys(responses).length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <Typography>No responses yet.</Typography>
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Response</TableCell>
                                <TableCell>Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(responses).map(([userId, response]) => {
                                const user = userData[userId];
                                return (
                                    <TableRow key={userId}>
                                        <TableCell>{user?.displayName || 'Unknown User'}</TableCell>
                                        <TableCell>{user?.email || 'N/A'}</TableCell>
                                        <TableCell>{response.answer}</TableCell>
                                        <TableCell>{new Date(response.timestamp?.toDate()).toLocaleString()}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ResponsesView;